import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Sun, Moon } from 'lucide-react';
import apiClient from '../utils/apiClient';
import { getImageUrl } from '../utils/apiConfig';
import { useAuth } from '../context/UnifiedAuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { useIsTablet } from '../hooks/useIsTablet';
import { useTheme } from '../hooks/useTheme';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import Icons from '../components/Icons';
import { Skeleton } from '../components/ui';

/**
 * Fantasy Golf Component
 * 
 * Main interface for making fantasy golf picks with authentication
 */

// Fantasy Scoring Configuration - Easy to modify
const FANTASY_SCORING = {
  quotaPerformance: {
    overQuota: 1,        // Points per stroke over quota
    underQuota: -1       // Points per stroke under quota
  },
  skins: 1.5,           // Points per skin
  ctps: 2,              // Points per CTP
  bonuses: {
    mostOverQuota: 2,   // Bonus for best quota performance (tied players get it too)
    leastToQuota: -2    // Penalty for worst quota performance (tied players get it too)
  }
};

// Payment Configuration - Easy to modify
const PAYMENT_CONFIG = {
  amount: 20,                              // Default payment amount
  paypalEmail: 'dollarbone@gmail.com',  // PayPal email/username
  venmoUsername: '@Jason-Newberg-2',         // Venmo username (include @)
  zelleEmail: 'dollarbone@gmail.com' // Zelle email/phone
};

const FantasyGolf = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [nextEventInfo, setNextEventInfo] = useState(null); // Info about the chronologically next event
  const [tiers, setTiers] = useState({ Tier1: [], Tier2: [], Tier3: [] });
  const [tierSnapshot, setTierSnapshot] = useState(null); // Store the tier snapshot from when tiers were first displayed
  const [selectedPicks, setSelectedPicks] = useState({ tier1: '', tier2: '', tier3: '' });
  const [submittedPicks, setSubmittedPicks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [timeUntilLockout, setTimeUntilLockout] = useState(null);
  const [isPicksLocked, setIsPicksLocked] = useState(false);
  const [timeUntilTierFreeze, setTimeUntilTierFreeze] = useState(null);
  const [areTiersFrozen, setAreTiersFrozen] = useState(false);
  const [showPayPalModal, setShowPayPalModal] = useState(false);
  const [showVenmoModal, setShowVenmoModal] = useState(false);
  const [showZelleModal, setShowZelleModal] = useState(false);
  const [showCashModal, setShowCashModal] = useState(false);
  const [serverTimeOffset, setServerTimeOffset] = useState(null); // Offset between server and client time (null = not synced yet)
  const [serverTimeSyncFailed, setServerTimeSyncFailed] = useState(false);
  const [isLoadingTiers, setIsLoadingTiers] = useState(false); // Guard against multiple simultaneous loads
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isCompactLayout = isMobile || isTablet;
  const [activeTierTab, setActiveTierTab] = useState('Tier1');
  const [isRulesExpanded, setIsRulesExpanded] = useState(false);
  const [isPaymentExpanded, setIsPaymentExpanded] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();

  // Create stable user reference for dependencies
  const userRef = useMemo(() => ({
    id: user?.id,
    email: user?.email,
    player_id: user?.player_id
  }), [user?.id, user?.email, user?.player_id]);

  // Function to get server time and calculate offset
  const fetchServerTimeOffset = useCallback(async (retryCount = 0) => {
    try {
      const clientTimeBefore = Date.now();
      const response = await apiClient.get('/fantasy/server-time', {
        timeout: 5000 // 5 second timeout
      });
      const clientTimeAfter = Date.now();

      const serverTime = new Date(response.data.serverTime).getTime();
      const estimatedClientTime = (clientTimeBefore + clientTimeAfter) / 2; // Account for request latency

      const offset = serverTime - estimatedClientTime;
      setServerTimeOffset(offset);
      setServerTimeSyncFailed(false); // Clear any previous sync failure

      console.log('Server time sync successful:', {
        serverTime: new Date(serverTime).toISOString(),
        clientTime: new Date(estimatedClientTime).toISOString(),
        offset: offset,
        offsetMinutes: Math.round(offset / 1000 / 60 * 10) / 10,
        attempt: retryCount + 1
      });
    } catch (error) {
      console.warn(`Failed to sync with server time (attempt ${retryCount + 1}):`, error);

      // Retry up to 3 times with exponential backoff
      if (retryCount < 2) {
        const backoffDelay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        setTimeout(() => {
          fetchServerTimeOffset(retryCount + 1);
        }, backoffDelay);
      } else {
        console.warn('Server time sync failed after 3 attempts, using local time');
        setServerTimeOffset(0); // Fallback to local time after all retries
        setServerTimeSyncFailed(true); // Flag that sync failed
      }
    }
  }, []);

  // Get current server time (adjusted client time)
  const getServerTime = useCallback(() => {
    return new Date(Date.now() + (serverTimeOffset || 0));
  }, [serverTimeOffset]);

  const fetchEvents = useCallback(async () => {
    try {
      // Fetch ALL upcoming events (not closed) - no fantasy filter initially
      const response = await apiClient.get('/admin/events?type=upcoming');
      // Handle new API format that returns { events: [...], meta: {...} }
      const eventsData = response.data?.events || response.data || [];
      const allUpcomingEvents = eventsData.filter(event => !event.closed); // Only open events

      if (allUpcomingEvents.length === 0) {
        // No upcoming events at all
        setEvents([]);
        setSelectedEvent(null);
        setNextEventInfo(null);
        setMessage('No upcoming events scheduled.');
        return;
      }

      // Sort by date to find the chronologically next event
      const sortedEvents = allUpcomingEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
      const nextEvent = sortedEvents[0];

      // Check if the next event has fantasy enabled
      if (nextEvent.fantasy_enabled) {
        // Next event is fantasy-enabled - show it normally
        setEvents([nextEvent]);
        setSelectedEvent(nextEvent.id.toString());
        setNextEventInfo(null);
        setMessage('');
      } else {
        // Next event exists but fantasy is disabled
        setEvents([]);
        setSelectedEvent(null);
        setNextEventInfo({
          course_name: nextEvent.course_name,
          date: nextEvent.date,
          id: nextEvent.id
        });
        setMessage('');
      }

    } catch (error) {
      setMessage('Failed to load events');
      setEvents([]);
      setSelectedEvent(null);
      setNextEventInfo(null);
    }
  }, []);

  // Fetch server time offset on component mount
  useEffect(() => {
    fetchServerTimeOffset(); // Always fetch server time offset
  }, []); // Only run once on mount

  // Fetch events when authentication changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchEvents();
    }
  }, [isAuthenticated]); // Removed fetchEvents since it has empty deps and should be stable

  // Helper function to get the correct player image URL
  const getPlayerImageUrl = (player) => {
    // If this player is linked to the current user and they have a profile picture, use that
    if (user?.player_id && player.id === user.player_id && user.profilePicture) {
      return getImageUrl(user.profilePicture);
    }
    // Otherwise use the player's image
    return getImageUrl(player.image_path);
  };

  // Helper function to calculate tier freeze time (24 hours before tee time)
  const calculateTierFreezeTime = (eventDate, teeTime) => {
    if (!eventDate || !teeTime) return null;

    // Parse the event date (e.g., "2025-08-03T04:00:00.000Z")
    const eventDateObj = new Date(eventDate);

    // Parse the tee time (e.g., "16:02:00")
    const [hours, minutes, seconds] = teeTime.split(':').map(Number);

    // Create the tee time datetime
    const teeTimeDate = new Date(eventDateObj);
    teeTimeDate.setHours(hours, minutes, seconds, 0);

    // Subtract 24 hours for tier freeze
    const tierFreezeTime = new Date(teeTimeDate.getTime() - 24 * 60 * 60 * 1000);

    return tierFreezeTime;
  };

  // Helper function to calculate lockout time (10 minutes after tee time)
  const calculateLockoutTime = (eventDate, teeTime) => {
    if (!eventDate || !teeTime) return null;

    // Parse the event date (e.g., "2025-08-03T04:00:00.000Z")
    const eventDateObj = new Date(eventDate);

    // Parse the tee time (e.g., "16:02:00")
    const [hours, minutes, seconds] = teeTime.split(':').map(Number);

    // Create the tee time datetime
    const teeTimeDate = new Date(eventDateObj);
    teeTimeDate.setHours(hours, minutes, seconds, 0);

    // Add 30 minutes for lockout
    const lockoutTime = new Date(teeTimeDate.getTime() + 30 * 60 * 1000);

    return lockoutTime;
  };

  // Helper function to format time remaining
  const formatTimeRemaining = (milliseconds) => {
    if (!milliseconds || isNaN(milliseconds) || milliseconds <= 0) return 'Picks are locked';

    const totalSeconds = Math.floor(milliseconds / 1000);
    const days = Math.floor(totalSeconds / (24 * 60 * 60));
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    const seconds = totalSeconds % 60;

    // Pad seconds to always show 2 digits
    const paddedSeconds = seconds.toString().padStart(2, '0');

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m ${paddedSeconds}s`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${paddedSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${paddedSeconds}s`;
    } else {
      return `${paddedSeconds}s remaining`;
    }
  };

  // Timer effect to update countdown for both tier freeze and picks lockout
  useEffect(() => {
    // Don't start timer until server time is synced
    if (serverTimeOffset === null) {
      return;
    }

    if (!selectedEvent) {
      setTimeUntilLockout(null);
      setIsPicksLocked(false);
      setTimeUntilTierFreeze(null);
      setAreTiersFrozen(false); // OK to reset when no event selected
      return;
    }

    const selectedEventData = events.find(e => e.id.toString() === selectedEvent.toString());
    if (!selectedEventData) return;

    const lockoutTime = calculateLockoutTime(selectedEventData.date, selectedEventData.tee_time);
    const tierFreezeTime = calculateTierFreezeTime(selectedEventData.date, selectedEventData.tee_time);
    if (!lockoutTime || !tierFreezeTime) return;

    const updateTimer = () => {
      const now = getServerTime(); // Use server time instead of local time
      const timeUntilPicksLockout = lockoutTime.getTime() - now.getTime();
      const timeUntilTierFreezeDeadline = tierFreezeTime.getTime() - now.getTime();

      setTimeUntilLockout(timeUntilPicksLockout);
      setIsPicksLocked(timeUntilPicksLockout <= 0);
      setTimeUntilTierFreeze(timeUntilTierFreezeDeadline);
      // Don't override areTiersFrozen here - let backend be authoritative
      // setAreTiersFrozen is now only set by fetchTiers() from backend response
    };

    // Update immediately
    updateTimer();

    // Set up interval to update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [selectedEvent, events, getServerTime, serverTimeOffset]);

  // Fetch tiers when event is selected and user data is ready
  useEffect(() => {
    const loadEventData = async () => {
      // Don't do anything while auth is still loading
      if (authLoading) {
        console.log('⏳ Auth still loading, waiting...');
        return;
      }

      // Wait for user data to be fully loaded
      if (!selectedEvent || !isAuthenticated) {
        console.log('⏳ Waiting for event selection or authentication...');
        return;
      }

      if (!user?.player_id) {
        console.log('⏳ User authenticated but no player_id linked');
        return;
      }

      console.log('✅ All conditions met, fetching tiers...');
      await fetchTiers(selectedEvent);
    };

    loadEventData();
  }, [selectedEvent, isAuthenticated, user?.player_id, authLoading]); // Include authLoading in dependencies

  const checkExistingPicks = async (eventId, tiersData, currentUser) => {
    try {
      const response = await apiClient.get(`/fantasy/picks/${eventId}`);

      console.log('🔍 checkExistingPicks - All picks:', response.data);
      console.log('🔍 checkExistingPicks - Current user:', {
        id: currentUser?.id,
        email: currentUser?.email,
        idString: currentUser?.id?.toString()
      });

      // Find current user's picks - try multiple matching strategies
      let userPicks = null;

      // First try user ID match (primary method for admin users)
      if (currentUser?.id) {
        userPicks = response.data.find(pick => pick.participant_id === currentUser.id.toString());
        console.log('🔍 Matching by ID:', currentUser.id.toString(), '→', userPicks ? 'FOUND' : 'NOT FOUND');
      }

      // If no match and user has email, try email match
      if (!userPicks && currentUser?.email) {
        userPicks = response.data.find(pick => pick.participant_id === currentUser.email);
      }

      // If still no match, try lowercase email match
      if (!userPicks && currentUser?.email) {
        userPicks = response.data.find(pick => pick.participant_id.toLowerCase() === currentUser.email.toLowerCase());
      }

      if (userPicks) {
        console.log('✅ Found user picks:', userPicks);
        // User has existing picks - populate the form
        setSelectedPicks({
          tier1: userPicks.tier1_pick?.toString() || '',
          tier2: userPicks.tier2_pick?.toString() || '',
          tier3: userPicks.tier3_pick?.toString() || ''
        });

        // For displaying existing picks, we need to get the player data from tier data
        // and check if they're still in the current event (haven't withdrawn)
        try {
          const playerIds = [userPicks.tier1_pick, userPicks.tier2_pick, userPicks.tier3_pick].filter(Boolean);

          if (playerIds.length === 3) {
            // Get player details from the tier data (which has calculated HPI fields)
            const allCurrentPlayers = [...tiersData.Tier1, ...tiersData.Tier2, ...tiersData.Tier3];
            const playerDetails = playerIds.map(playerId => {
              return allCurrentPlayers.find(p => p.id === parseInt(playerId)) || null;
            });

            const existingPickDetails = {
              tier1: playerDetails[0] ? {
                ...playerDetails[0],
                isWithdrawn: !allCurrentPlayers.some(p => p.id === playerDetails[0].id)
              } : null,
              tier2: playerDetails[1] ? {
                ...playerDetails[1],
                isWithdrawn: !allCurrentPlayers.some(p => p.id === playerDetails[1].id)
              } : null,
              tier3: playerDetails[2] ? {
                ...playerDetails[2],
                isWithdrawn: !allCurrentPlayers.some(p => p.id === playerDetails[2].id)
              } : null
            };

            // Show picks even if some players have withdrawn - we'll style them differently
            if (existingPickDetails.tier1 && existingPickDetails.tier2 && existingPickDetails.tier3) {
              setSubmittedPicks(existingPickDetails);

              // Check if any players have withdrawn and show appropriate message
              const withdrawnPlayers = [existingPickDetails.tier1, existingPickDetails.tier2, existingPickDetails.tier3]
                .filter(player => player.isWithdrawn);

              if (withdrawnPlayers.length > 0) {
                const withdrawnNames = withdrawnPlayers.map(p => p.name).join(', ');
                setMessage(`${withdrawnNames} ${withdrawnPlayers.length === 1 ? 'has' : 'have'} withdrawn from this event and will not earn fantasy points`);
              } else {
                setMessage('');
              }

              // Check for tier changes - but only warn if they're problematic
              // With the new append-only system (24h freeze), existing picks shouldn't move tiers
              const tier1Pick = existingPickDetails.tier1;
              const tier2Pick = existingPickDetails.tier2;
              const tier3Pick = existingPickDetails.tier3;

              const tier1PlayerIds = tiersData.Tier1.map(p => p.id);
              const tier2PlayerIds = tiersData.Tier2.map(p => p.id);
              const tier3PlayerIds = tiersData.Tier3.map(p => p.id);

              let tierShiftWarning = '';

              // Only warn about tier shifts if tiers should be frozen (24h+ before tee time has passed)
              if (areTiersFrozen) {
                if (tier1Pick && !tier1Pick.isWithdrawn && !tier1PlayerIds.includes(tier1Pick.id)) {
                  tierShiftWarning += `Your Tier 1 pick (${tier1Pick.name}) has moved to a different tier. `;
                }
                if (tier2Pick && !tier2Pick.isWithdrawn && !tier2PlayerIds.includes(tier2Pick.id)) {
                  tierShiftWarning += `Your Tier 2 pick (${tier2Pick.name}) has moved to a different tier. `;
                }
                if (tier3Pick && !tier3Pick.isWithdrawn && !tier3PlayerIds.includes(tier3Pick.id)) {
                  tierShiftWarning += `Your Tier 3 pick (${tier3Pick.name}) has moved to a different tier. `;
                }

                if (tierShiftWarning) {
                  if (!withdrawnPlayers.length) {
                    setMessage(`${tierShiftWarning}This should not happen after tier freeze. Please contact admin.`);
                  }
                }
              }
            } else {
              // Some players couldn't be fetched - show warning but don't clear picks
              setSubmittedPicks(null);
              setMessage('Some of your previously selected players may no longer be available');
            }
          } else {
            setSubmittedPicks(null);
            setMessage('');
          }
        } catch (error) {
          setSubmittedPicks(null);
          setMessage('');
        }
      } else {
        console.log('❌ No picks found for user');
        // No existing picks found - could be first time or picks were reset
        // Check if user previously submitted picks by looking at localStorage as a hint
        // Try multiple storage keys to account for different user authentication methods
        const userIdentifiers = [
          currentUser?.id,
          currentUser?.email,
          currentUser?.email?.toLowerCase()
        ].filter(Boolean);

        let previouslySubmitted = false;
        let storageKey = null;

        for (const identifier of userIdentifiers) {
          const testKey = `fantasy_picks_submitted_${eventId}_${identifier}`;
          if (localStorage.getItem(testKey)) {
            previouslySubmitted = true;
            storageKey = testKey;
            break;
          }
        }

        // Clear any stored submission state since no picks exist
        if (previouslySubmitted && storageKey) {
          localStorage.removeItem(storageKey);
          setMessage('⚠️ Your fantasy picks were automatically reset due to new player registrations causing tier changes. Please make new selections to ensure fair play. You should have received an email notification with details.');
        }

        // Set to empty state for display
        setSubmittedPicks(null);
        setSelectedPicks({ tier1: '', tier2: '', tier3: '' });
        if (!previouslySubmitted) {
          setMessage('');
        }
      }
    } catch (error) {
      console.error('❌ Error checking existing picks:', error);
      // No existing picks found, or error - set to empty state
      setSubmittedPicks(null);
      setSelectedPicks({ tier1: '', tier2: '', tier3: '' });
      setMessage('');
    }
  };

  const fetchTiers = async (eventId) => {
    try {
      console.log(`🔄 fetchTiers called for event ${eventId}`);
      console.trace('🔍 fetchTiers call stack:');
      setLoading(true);
      const response = await apiClient.get(`/fantasy/tiers/${eventId}`);
      const tiersData = response.data.tiers || { Tier1: [], Tier2: [], Tier3: [] };
      const backendAreTiersFrozen = response.data.areTiersFrozen;
      const backendTierFreezeTime = response.data.tierFreezeTime;

      console.log(`[Frontend] Received tiers for event ${eventId}: areTiersFrozen=${backendAreTiersFrozen}, tierFreezeTime=${backendTierFreezeTime}`);
      console.log(`[Frontend] Tiers data:`, tiersData);

      // Ensure all tier arrays exist
      const safeTiersData = {
        Tier1: tiersData.Tier1 || [],
        Tier2: tiersData.Tier2 || [],
        Tier3: tiersData.Tier3 || []
      };

      setTiers(safeTiersData);

      // Use backend's authoritative tier frozen status instead of client-side calculation
      setAreTiersFrozen(backendAreTiersFrozen);

      // Capture tier snapshot when tiers are first displayed to the user
      // This ensures we validate against the exact tier assignments the user saw
      const snapshot = {
        Tier1: safeTiersData.Tier1.map(p => p.id),
        Tier2: safeTiersData.Tier2.map(p => p.id),
        Tier3: safeTiersData.Tier3.map(p => p.id)
      };
      console.log('📸 Setting tier snapshot:', snapshot);
      setTierSnapshot(snapshot);

      // Check for existing picks if user is authenticated
      if (isAuthenticated && user) {
        console.log('📋 Checking for existing picks...');
        await checkExistingPicks(eventId, safeTiersData, user);
      } else {
        // If not authenticated, clear the picks state
        setSubmittedPicks(null);
      }
    } catch (error) {
      setMessage('Failed to load tiers');
      setSubmittedPicks(null); // Clear picks on error
    } finally {
      setLoading(false);
    }
  };

  const handlePickChange = (tier, playerId) => {
    setSelectedPicks(prev => ({
      ...prev,
      [tier]: playerId
    }));
  };

  // Helper function to get player info by ID
  const getPlayerById = (playerId) => {
    const allPlayers = [...tiers.Tier1, ...tiers.Tier2, ...tiers.Tier3];
    return allPlayers.find(player => player.id === parseInt(playerId));
  };

  // Helper function to calculate Hacker Performance Index (HPI)
  const calculateHPI = (player) => {
    if (!player) return 0;

    // Ensure all values are numbers to avoid NaN
    const quotaScore = parseFloat(player.avg_quota || player.current_quota || 0) || 0;
    const avgSkins = parseFloat(player.avg_skins || 0) || 0;
    const avgCtps = parseFloat(player.avg_ctps || 0) || 0;

    // Performance multipliers based on season averages
    const skinsMultiplier = avgSkins * 5;
    const ctpMultiplier = avgCtps * 8;

    // Total HPI
    const hpi = quotaScore + skinsMultiplier + ctpMultiplier;

    // Ensure result is a valid number
    const result = Math.round(hpi * 10) / 10;
    return isNaN(result) ? 0 : result;
  };

  const submitPicks = async () => {
    if (!selectedEvent) {
      setMessage('Please select an event');
      return;
    }

    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    // Check if picks are locked
    if (isPicksLocked) {
      setMessage('Picks are locked - the deadline has passed (30 minutes after tee time)');
      return;
    }

    // Ensure we have a tier snapshot (this should always be available after fetchTiers)
    if (!tierSnapshot) {
      setMessage('Tier data not available. Please refresh the page and try again.');
      return;
    }

    // Debug logging to understand the submission issue
    console.log('🎯 Submitting picks with tierSnapshot:', {
      eventId: selectedEvent,
      tierSnapshot,
      picks: [selectedPicks.tier1, selectedPicks.tier2, selectedPicks.tier3]
    });

    const picks = [selectedPicks.tier1, selectedPicks.tier2, selectedPicks.tier3];
    if (picks.some(pick => !pick)) {
      setMessage('Please select one player from each tier');
      return;
    }

    // Get player names for confirmation
    const selectedPlayerDetails = {
      tier1: getPlayerById(selectedPicks.tier1),
      tier2: getPlayerById(selectedPicks.tier2),
      tier3: getPlayerById(selectedPicks.tier3)
    };

    try {
      setLoading(true);

      await apiClient.post('/fantasy/picks', {
        eventId: selectedEvent,
        picks: picks.map(Number),
        // Send the tier snapshot that was captured when tiers were first displayed to the user
        tierSnapshot: tierSnapshot
      });

      setSubmittedPicks(selectedPlayerDetails);
      setMessage('Picks submitted successfully!');
      setSelectedPicks({ tier1: '', tier2: '', tier3: '' });

      // Store flag that user successfully submitted picks for this event
      // Store with multiple identifiers to improve detection reliability
      const userIdentifiers = [
        user?.id,
        user?.email,
        user?.email?.toLowerCase()
      ].filter(Boolean);

      userIdentifiers.forEach(identifier => {
        const storageKey = `fantasy_picks_submitted_${selectedEvent}_${identifier}`;
        localStorage.setItem(storageKey, 'true');
      });
    } catch (error) {

      if (error.statusCode === 401) {
        setMessage('Please log in to submit picks');
        setShowAuthModal(true);
      } else {
        // Log error details for debugging
        console.error('🚨 Pick submission error:', {
          statusCode: error.statusCode,
          message: error.message,
          errors: error.errors,
          currentTierSnapshot: tierSnapshot
        });

        // DO NOT refresh tiers after error - this was causing the tier reset problem

        setMessage(error.message || 'Failed to submit picks');
      }
    } finally {
      setLoading(false);
    }
  };

  const TierSection = ({ tierName, players, selectedPick, onPickChange }) => {
    return (
      <div className={`bg-white dark:bg-navy-800 rounded-xl shadow-lg border border-gray-200 dark:border-navy-700 p-4 mb-4 ${isPicksLocked ? 'opacity-60' : ''}`}>
        <h3 className="text-lg font-bold text-forest-900 dark:text-white mb-3 text-center">
          {tierName} ({players.length} players)
        </h3>
        {isPicksLocked && (
          <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-3 text-center">
            Picks are now locked.
          </p>
        )}

        {/* Table layout without scrolling container */}
        <div>
          {/* Table Headers */}
          <div className="flex items-center space-x-3 mb-2 pb-2 border-b border-gray-200 dark:border-gray-600">
            <div className="w-6"></div> {/* Radio button space */}
            <div className="w-8"></div> {/* Profile image space */}
            <div className="flex-1 flex items-center min-w-0">
              <div className="flex-1 min-w-0 pr-2">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Player
                </span>
              </div>
              <div className="w-16 text-center flex-shrink-0">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Quota
                </span>
              </div>
              <div className="w-16 text-center flex-shrink-0">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  HPI
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {players.map(player => {
              const isCurrentUser = user?.player_id && player.id === user.player_id;
              const isDisabled = isCurrentUser || isPicksLocked;

              return (
                <label
                  key={player.id}
                  className={`flex items-center space-x-2 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer active:bg-gray-50 dark:active:bg-navy-700'
                    }`}
                >
                  <input
                    type="radio"
                    name={tierName.toLowerCase()}
                    value={player.id}
                    checked={selectedPick === player.id.toString()}
                    onChange={(e) => !isPicksLocked && onPickChange(tierName.toLowerCase().replace(' ', ''), e.target.value)}
                    disabled={isDisabled}
                    className={`text-blue-600 focus:ring-blue-500 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                  />
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {(player.image_path || (user?.player_id === player.id && user.profilePicture)) && (
                      <img
                        src={getPlayerImageUrl(player)}
                        alt={player.name}
                        className="w-8 h-8 rounded-full flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 flex items-center min-w-0">
                      <div className="flex-1 min-w-0 pr-2">
                        <span className={`font-medium text-sm dark:text-gray-200 ${isCurrentUser ? 'text-blue-600 dark:text-blue-400' : ''}`}>
                          {player.name}
                          {isCurrentUser && <span className="text-xs ml-1">(You)</span>}
                        </span>
                      </div>
                      <div className="w-16 text-center flex-shrink-0">
                        <span className="text-gray-500 dark:text-gray-400 text-sm">
                          {player.current_quota}
                        </span>
                      </div>
                      <div className="w-16 text-center flex-shrink-0">
                        <span className="text-blue-600 dark:text-blue-400 text-sm font-medium">
                          {calculateHPI(player)}
                        </span>
                      </div>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-navy-950 flex flex-col transition-colors duration-300">
      <Navbar />

      <main className="flex-grow">
        {/* Hero Header Section */}
        <div className="bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-navy-800">
          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-display font-bold text-forest-900 dark:text-white tracking-tight">
                  Fantasy Golf
                </h1>
                <p className="text-gray-600 dark:text-navy-400 mt-1">
                  Fireball Fantasy Fiasco - Make Your Picks
                </p>
              </div>

              <div className="flex items-center gap-4">
                {/* Dark Mode Toggle */}
                <button
                  onClick={toggleTheme}
                  className="
                    flex items-center gap-2 px-4 py-2 rounded-full
                    bg-gray-100 dark:bg-navy-800
                    text-gray-700 dark:text-gray-200
                    hover:bg-gray-200 dark:hover:bg-navy-700
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-navy-900
                    transition-colors duration-200
                    border border-gray-200 dark:border-navy-700
                  "
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-pressed={isDarkMode}
                >
                  {isDarkMode ? (
                    <>
                      <Sun className="w-5 h-5 text-gold-400" />
                      <span className="text-sm font-medium hidden sm:inline">Light</span>
                    </>
                  ) : (
                    <>
                      <Moon className="w-5 h-5 text-navy-600" />
                      <span className="text-sm font-medium hidden sm:inline">Dark</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Event Selection */}
          <div className="bg-white dark:bg-navy-800 border border-gray-200 dark:border-navy-700 rounded-xl shadow-lg p-6 mb-6">



          <div className="grid grid-cols-1 gap-4 mb-4">
            {/* Combined Event Display with Countdown Timer */}
            {selectedEvent && events.length > 0 ? (
              <div className={`p-4 rounded-lg border ${isPicksLocked
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50'
                : timeUntilLockout !== null && timeUntilLockout < 60 * 60 * 1000
                  ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/50'
                  : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50'
                }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="text-forest-600 dark:text-forest-400">
                      <Icons.Golf />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 dark:text-gray-100">Next Event</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {events.find(e => e.id.toString() === selectedEvent)?.course_name} - {' '}
                        {new Date(events.find(e => e.id.toString() === selectedEvent)?.date).toLocaleDateString()}
                      </p>
                      {/* Tier freeze status */}
                      {areTiersFrozen && (
                        <p className="text-xs text-forest-700 dark:text-forest-400 font-medium flex items-center mt-1">
                          <Icons.Lock />
                          <span className="ml-1">Tiers frozen - fair picks guaranteed</span>
                        </p>
                      )}
                      {!areTiersFrozen && timeUntilTierFreeze !== null && timeUntilTierFreeze > 0 && (
                        <p className="text-xs text-amber-700 dark:text-amber-400 font-medium flex items-center mt-1">
                          <Icons.Clock />
                          <span className="ml-1">Tiers freeze in {formatTimeRemaining(timeUntilTierFreeze)}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Timer Section */}
                  {serverTimeOffset === null ? (
                    <div className="space-y-2">
                      <Skeleton className="h-3 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  ) : timeUntilLockout !== null ? (
                    <div className="flex items-center space-x-2">
                      <div className="text-right">
                        <p className={`font-semibold text-sm transition-colors duration-300 ${isPicksLocked
                          ? 'text-red-800 dark:text-red-400'
                          : timeUntilLockout < 60 * 60 * 1000
                            ? 'text-orange-800 dark:text-orange-400'
                            : 'text-forest-800 dark:text-forest-400'
                          }`}>
                          {isPicksLocked ? 'Picks Locked' : 'Pick Deadline'}
                        </p>
                        <p className={`text-sm font-mono font-semibold transition-colors duration-300 ${isPicksLocked
                          ? 'text-red-600 dark:text-red-400'
                          : timeUntilLockout < 60 * 60 * 1000
                            ? 'text-orange-600 dark:text-orange-400'
                            : 'text-forest-600 dark:text-forest-400'
                          }`}>
                          {formatTimeRemaining(timeUntilLockout)}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : nextEventInfo ? (
              /* Next event exists but fantasy is disabled */
              <div className="p-4 rounded-lg border bg-amber-50 border-amber-200">
                <div className="flex items-center space-x-3">
                  <div className="text-amber-600">
                    <Icons.Clock />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800">Fantasy Not Available Yet</h3>
                    <p className="text-sm text-amber-700">
                      The next event is {nextEventInfo.course_name} on {new Date(nextEventInfo.date).toLocaleDateString()},
                      but fantasy picks are not enabled for this event yet.
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      Check back later or contact an admin if you think this is an error.
                    </p>
                  </div>
                </div>
              </div>
            ) : message ? (
              /* Show general message (like "No upcoming events") */
              <div className="p-4 rounded-lg border bg-gray-50 border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="text-gray-600">
                    <Icons.Calendar />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-700">No Events Available</h3>
                    <p className="text-sm text-gray-600">{message}</p>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Authentication Status */}
            {!isAuthenticated && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-amber-700">
                    <Icons.AlertTriangle className="w-5 h-5 mr-2" />
                    <span className="font-medium">Please log in to make picks</span>
                  </div>
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Login
                  </button>
                </div>
              </div>
            )}
          </div>

          {message && (
            <div className={`p-3 rounded-md mb-4 ${message.includes('success') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
              {message}
            </div>
          )}

          {/* Display current picks for selected event */}
          {selectedEvent && isAuthenticated && user?.player_id && events.length > 0 && (
            <div className="bg-blue-50 dark:bg-navy-700 border border-blue-200 dark:border-navy-600 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-bold text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2">
                <Icons.Target />
                Your Fantasy Picks
              </h3>
              {submittedPicks ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className={`bg-white dark:bg-navy-800 rounded-lg p-3 border ${submittedPicks.tier1?.isWithdrawn ? 'border-red-200 bg-red-50 dark:bg-red-900/20' : 'border-blue-100 dark:border-navy-600'}`}>
                      <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-2">Tier 1 Pick</h4>
                      <div className="flex items-center space-x-3">
                        {submittedPicks.tier1?.image_path && (
                          <img
                            src={getPlayerImageUrl(submittedPicks.tier1)}
                            alt={submittedPicks.tier1.name}
                            className={`w-12 h-12 rounded-full ${submittedPicks.tier1.isWithdrawn ? 'opacity-50 grayscale' : ''}`}
                          />
                        )}
                        <div>
                          <p className={`font-medium ${submittedPicks.tier1?.isWithdrawn ? 'text-gray-500 line-through' : ''}`}>
                            {submittedPicks.tier1?.name}
                            {submittedPicks.tier1?.isWithdrawn && <span className="text-red-600 text-xs ml-2">(Withdrawn)</span>}
                          </p>
                          <p className={`text-sm ${submittedPicks.tier1?.isWithdrawn ? 'text-gray-400' : 'text-gray-600'}`}>
                            Quota: {submittedPicks.tier1?.current_quota || 'N/A'}
                          </p>
                          <p className={`text-sm ${submittedPicks.tier1?.isWithdrawn ? 'text-gray-400' : 'text-gray-600'}`}>
                            HPI: {calculateHPI(submittedPicks.tier1)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className={`bg-white dark:bg-navy-800 rounded-lg p-3 border ${submittedPicks.tier2?.isWithdrawn ? 'border-red-200 bg-red-50 dark:bg-red-900/20' : 'border-blue-100 dark:border-navy-600'}`}>
                      <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-2">Tier 2 Pick</h4>
                      <div className="flex items-center space-x-3">
                        {submittedPicks.tier2?.image_path && (
                          <img
                            src={getPlayerImageUrl(submittedPicks.tier2)}
                            alt={submittedPicks.tier2.name}
                            className={`w-12 h-12 rounded-full ${submittedPicks.tier2.isWithdrawn ? 'opacity-50 grayscale' : ''}`}
                          />
                        )}
                        <div>
                          <p className={`font-medium ${submittedPicks.tier2?.isWithdrawn ? 'text-gray-500 line-through' : ''}`}>
                            {submittedPicks.tier2?.name}
                            {submittedPicks.tier2?.isWithdrawn && <span className="text-red-600 text-xs ml-2">(Withdrawn)</span>}
                          </p>
                          <p className={`text-sm ${submittedPicks.tier2?.isWithdrawn ? 'text-gray-400' : 'text-gray-600'}`}>
                            Quota: {submittedPicks.tier2?.current_quota || 'N/A'}
                          </p>
                          <p className={`text-sm ${submittedPicks.tier2?.isWithdrawn ? 'text-gray-400' : 'text-gray-600'}`}>
                            HPI: {calculateHPI(submittedPicks.tier2)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className={`bg-white dark:bg-navy-800 rounded-lg p-3 border ${submittedPicks.tier3?.isWithdrawn ? 'border-red-200 bg-red-50 dark:bg-red-900/20' : 'border-blue-100 dark:border-navy-600'}`}>
                      <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-2">Tier 3 Pick</h4>
                      <div className="flex items-center space-x-3">
                        {submittedPicks.tier3?.image_path && (
                          <img
                            src={getPlayerImageUrl(submittedPicks.tier3)}
                            alt={submittedPicks.tier3.name}
                            className={`w-12 h-12 rounded-full ${submittedPicks.tier3.isWithdrawn ? 'opacity-50 grayscale' : ''}`}
                          />
                        )}
                        <div>
                          <p className={`font-medium ${submittedPicks.tier3?.isWithdrawn ? 'text-gray-500 line-through' : ''}`}>
                            {submittedPicks.tier3?.name}
                            {submittedPicks.tier3?.isWithdrawn && <span className="text-red-600 text-xs ml-2">(Withdrawn)</span>}
                          </p>
                          <p className={`text-sm ${submittedPicks.tier3?.isWithdrawn ? 'text-gray-400' : 'text-gray-600'}`}>
                            Quota: {submittedPicks.tier3?.current_quota || 'N/A'}
                          </p>
                          <p className={`text-sm ${submittedPicks.tier3?.isWithdrawn ? 'text-gray-400' : 'text-gray-600'}`}>
                            HPI: {calculateHPI(submittedPicks.tier3)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-white dark:bg-navy-800 rounded-lg p-6 border border-blue-100 dark:border-navy-600">
                  <div className="text-center text-gray-500 dark:text-gray-400">
                    <div className="flex justify-center mb-2 text-gray-400">
                      <Icons.Edit />
                    </div>
                    <p className="font-medium">No submitted picks for this event</p>
                    <p className="text-sm mt-1">Select players from each tier below to make your picks</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Account Linking Required Message */}
        {isAuthenticated && !user?.player_id && (
          <div className="text-center py-12">
            <div className="flex justify-center mb-4 text-amber-600">
              <Icons.AlertTriangle className="w-12 h-12" />
            </div>
            <h3 className="text-xl font-semibold text-amber-700 mb-2">Account Linking Required</h3>
            <p className="text-amber-600 mb-6 max-w-md mx-auto">
              To make fantasy picks, you need to link your account to a player profile.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-amber-800 text-sm font-medium mb-2">How to link your account:</p>
              <ol className="text-amber-700 text-sm text-left space-y-1">
                <li>1. Hover over you profile name in the top right</li>
                <li>2. Select Player Profile</li>
                <li>3. Select your Player name</li>
                <li>4. Click Link Account button</li>
              </ol>
            </div>
          </div>
        )}

        {/* Tier Selection */}
        {selectedEvent && !loading && isAuthenticated && user?.player_id && events.length > 0 && (
          isCompactLayout ? (
            <div className="mb-6">
              {/* Mobile Tabs */}
              <div className="flex rounded-lg bg-gray-100 dark:bg-navy-700 p-1 mb-4">
                {['Tier1', 'Tier2', 'Tier3'].map((tier) => {
                  const tierKey = tier.toLowerCase();
                  const hasPick = Boolean(selectedPicks[tierKey]);
                  return (
                  <button
                    key={tier}
                    onClick={() => setActiveTierTab(tier)}
                    className={`flex-1 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center space-x-2 ${activeTierTab === tier
                      ? 'bg-white dark:bg-navy-600 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                  >
                    <span>{tier.replace('Tier', 'Tier ')}</span>
                    {hasPick && <Icons.Check className="w-4 h-4 text-blue-500" aria-hidden="true" />}
                  </button>
                  );
                })}
              </div>

              {/* Active Tier Content */}
              {tiers[activeTierTab] && (
                <TierSection
                  tierName={activeTierTab.replace('Tier', 'Tier ')}
                  players={tiers[activeTierTab]}
                  selectedPick={selectedPicks[activeTierTab.toLowerCase()]}
                  onPickChange={handlePickChange}
                />
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <TierSection
                tierName="Tier 1"
                players={tiers.Tier1}
                selectedPick={selectedPicks.tier1}
                onPickChange={handlePickChange}
              />
              <TierSection
                tierName="Tier 2"
                players={tiers.Tier2}
                selectedPick={selectedPicks.tier2}
                onPickChange={handlePickChange}
              />
              <TierSection
                tierName="Tier 3"
                players={tiers.Tier3}
                selectedPick={selectedPicks.tier3}
                onPickChange={handlePickChange}
              />
            </div>
          )
        )}

        {/* Loading State */}
        {loading && (
          <div className={`${isCompactLayout ? 'space-y-3' : 'grid grid-cols-1 lg:grid-cols-3 gap-4'} py-4`}>
            {(isCompactLayout ? ['Tier'] : ['Tier 1', 'Tier 2', 'Tier 3']).map((label, index) => (
              <div key={label} className="bg-white dark:bg-navy-900/70 border border-gray-100 dark:border-gray-800 rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Skeleton className="w-6 h-6 rounded-full" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  {!isCompactLayout && <span className="text-xs text-gray-400">Tier {index + 1}</span>}
                </div>
                <div className="space-y-2">
                  {[1, 2, 3].map(row => (
                    <div key={row} className="flex items-center space-x-3">
                      <Skeleton className="w-5 h-5 rounded-full" />
                      <Skeleton className="w-10 h-10 rounded-full" />
                      <div className="flex-1 space-y-1">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                      <Skeleton className="h-4 w-10" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Not Authenticated Message */}
        {selectedEvent && !isAuthenticated && events.length > 0 && (
          <div className="text-center py-12">
            <div className="flex justify-center mb-4 text-gray-600">
              <Icons.Lock />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Authentication Required</h3>
            <p className="text-gray-600 mb-6">Please log in to view tiers and make your fantasy picks</p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Login to Continue
            </button>
          </div>
        )}

        {/* Fantasy Not Available Message for Authenticated Users */}
        {nextEventInfo && isAuthenticated && (
          <div className="text-center py-12">
            <div className="flex justify-center mb-4 text-gray-600">
              <Icons.Clock />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Fantasy Picks Not Available</h3>
            <p className="text-gray-600 mb-4">
              The next event ({nextEventInfo.course_name} on {new Date(nextEventInfo.date).toLocaleDateString()})
              does not have fantasy picks enabled yet.
            </p>
            <p className="text-sm text-gray-500">
              This typically happens when the event is still being set up. Check back later!
            </p>
          </div>
        )}

        {/* Projected Fantasy Points Preview */}
        {selectedEvent && isAuthenticated && user?.player_id && events.length > 0 && (selectedPicks.tier1 || selectedPicks.tier2 || selectedPicks.tier3) && (
          <div className="bg-white dark:bg-navy-800 border border-gray-200 dark:border-navy-700 rounded-xl p-6 mb-6 shadow-lg">
            <h3 className="text-lg font-bold text-forest-900 dark:text-white mb-4 flex items-center">
              <Icons.Crystal />
              <span className="ml-2">Projected Fantasy Points</span>
            </h3>
            {(() => {
              // Calculate partial projections for selected picks
              const tierPicks = [
                { tierName: 'Tier 1', playerId: selectedPicks.tier1, tierIndex: 0 },
                { tierName: 'Tier 2', playerId: selectedPicks.tier2, tierIndex: 1 },
                { tierName: 'Tier 3', playerId: selectedPicks.tier3, tierIndex: 2 }
              ];

              const projectionDetails = [];
              let totalProjectedPoints = 0;

              tierPicks.forEach(({ tierName, playerId, tierIndex }) => {
                if (playerId) {
                  const player = getPlayerById(playerId);
                  if (player) {
                    // Calculate projection based on player's actual performance data
                    let projectedQuotaPerf = 0;
                    let projectedSkins = parseFloat(player.avg_skins) || 0;
                    let projectedCtps = parseFloat(player.avg_ctps) || 0;

                    // Sophisticated quota performance projection based on recent performance
                    // Compare average score over last 5 events to current quota
                    const currentQuota = player.current_quota || 0;
                    // Use last_5_avg_quota if available, otherwise fall back to season avg_quota
                    // If neither available, assume they'll meet their quota
                    const recentAverage = player.last_5_avg_quota || player.avg_quota || currentQuota;

                    // If they're averaging higher scores than their quota, they're projected to go over
                    // If they're averaging lower scores than their quota, they're projected to go under
                    // Cap the projection at reasonable limits (-6 to +6) to avoid extreme predictions
                    const rawProjection = recentAverage - currentQuota;
                    projectedQuotaPerf = Math.max(-6, Math.min(6, Math.round(rawProjection)));

                    // Use actual season averages for skins and CTPs
                    // No need to estimate based on tier anymore!

                    // Calculate projected points using actual performance metrics
                    let projectedPts = 0;
                    projectedPts += projectedQuotaPerf * (projectedQuotaPerf > 0 ? FANTASY_SCORING.quotaPerformance.overQuota : Math.abs(FANTASY_SCORING.quotaPerformance.underQuota));
                    projectedPts += projectedSkins * FANTASY_SCORING.skins;
                    projectedPts += projectedCtps * FANTASY_SCORING.ctps;

                    projectionDetails.push({
                      tierName,
                      name: player.name,
                      quota: player.current_quota,
                      avgQuota: player.avg_quota,
                      last5AvgQuota: recentAverage,
                      hpi: calculateHPI(player),
                      projectedQuotaPerf,
                      projectedSkins: projectedSkins.toFixed(1),
                      projectedCtps: projectedCtps.toFixed(1),
                      projectedPts: projectedPts.toFixed(1)
                    });

                    totalProjectedPoints += projectedPts;
                  }
                } else {
                  // Show placeholder for unselected tier
                  projectionDetails.push({
                    tierName,
                    name: null,
                    quota: null,
                    projectedQuotaPerf: null,
                    projectedSkins: null,
                    projectedCtps: null,
                    projectedPts: null
                  });
                }
              });

              return (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {projectionDetails.map((detail, idx) => (
                      <div key={idx} className={`bg-gray-50 dark:bg-navy-700 rounded-lg p-4 border ${detail.name ? 'border-blue-200 dark:border-navy-600' : 'border-gray-200 dark:border-navy-600'}`}>
                        <h4 className={`font-semibold mb-2 ${detail.name ? 'text-blue-700 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}>
                          {detail.tierName}: {detail.name || 'No selection'}
                        </h4>
                        {detail.name ? (
                          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                            <div>Current Quota: {detail.quota}</div>

                            <div>HPI Score: <span className="font-medium text-blue-600 dark:text-blue-400">{detail.hpi}</span></div>
                            <div className="border-t border-gray-200 dark:border-navy-600 pt-1 mt-1">
                              <div>Projected vs Quota: {detail.projectedQuotaPerf > 0 ? '+' : ''}{detail.projectedQuotaPerf}</div>
                              <div>Expected Skins: {detail.projectedSkins}</div>
                              <div>Expected CTPs: {detail.projectedCtps}</div>
                            </div>
                            <div className="font-semibold text-blue-600 dark:text-blue-400 border-t border-gray-200 dark:border-navy-600 pt-1 mt-2">
                              Projected Points: {detail.projectedPts}
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-400 dark:text-gray-500 space-y-1">
                            <div>Select a player to see projection</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="text-center bg-gradient-to-r from-forest-50 to-blue-50 dark:from-navy-700 dark:to-navy-700 rounded-lg p-4 border-2 border-forest-300 dark:border-forest-600">
                    <div className="text-2xl font-bold text-forest-700 dark:text-forest-400">
                      {projectionDetails.filter(d => d.name).length === 3 ? 'Total ' : 'Partial '}
                      Projected Points: {totalProjectedPoints.toFixed(1)}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      *Projections powered by our quantum AI trained on 1,000 years of golf data and breakfast choices. Results may vary based on solar flares and lucky sock alignment.
                    </p>
                    {projectionDetails.filter(d => d.name).length < 3 && (
                      <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                        Complete all three picks to see total projection
                      </p>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Submit Button */}
        {selectedEvent && isAuthenticated && user?.player_id && events.length > 0 && (
          <div className="text-center mb-8">
            <button
              onClick={submitPicks}
              disabled={loading || isPicksLocked}
              className={`font-bold py-3 px-8 rounded-lg disabled:opacity-50 ${isPicksLocked
                ? 'bg-red-600 hover:bg-red-700 text-white cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
            >
              {loading ? 'Submitting...' : isPicksLocked ? 'Picks Locked' : 'Submit Picks'}
            </button>
            {isPicksLocked && (
              <p className="text-red-600 text-sm mt-2">
                The deadline for picks has passed (30 minutes after tee time)
              </p>
            )}
          </div>
        )}

        {/* Authentication Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />

        {/* Rules Section - Collapsible */}
        <div className="bg-white dark:bg-navy-800 rounded-xl mt-6 border border-gray-200 dark:border-navy-700 shadow-lg overflow-hidden">
          <button
            onClick={() => setIsRulesExpanded(!isRulesExpanded)}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors"
          >
            <h3 className="text-lg font-bold text-forest-900 dark:text-white flex items-center gap-2">
              <Icons.Settings />
              Fantasy Scoring Rules
            </h3>
            <div className={`text-forest-600 dark:text-forest-400 transition-transform duration-200 ${isRulesExpanded ? 'rotate-180' : ''}`}>
              <Icons.ChevronDown />
            </div>
          </button>

          {isRulesExpanded && (
            <div className="px-6 pb-6 border-t border-gray-200 dark:border-navy-700">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mt-4">
                <div>
                  <h4 className="font-semibold text-forest-700 dark:text-forest-400 mb-2">Performance Points</h4>
                  <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                    <li className="flex items-center gap-2">
                      <Icons.Chart />
                      Quota performance: +{FANTASY_SCORING.quotaPerformance.overQuota} per point over, {FANTASY_SCORING.quotaPerformance.underQuota} per point under
                    </li>
                    <li className="flex items-center gap-2">
                      <Icons.Target />
                      Each skin: +{FANTASY_SCORING.skins} points
                    </li>
                    <li className="flex items-center gap-2">
                      <Icons.Location />
                      Each CTP: +{FANTASY_SCORING.ctps} points
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-forest-700 dark:text-forest-400 mb-2">Bonus/Penalty Points</h4>
                  <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                    <li className="flex items-center gap-2">
                      <Icons.Trophy />
                      Best quota performance: +{FANTASY_SCORING.bonuses.mostOverQuota} points (tied players all get bonus)
                    </li>
                    <li className="flex items-center gap-2">
                      <Icons.Frown />
                      Worst quota performance: {FANTASY_SCORING.bonuses.leastToQuota} points (tied players all get penalty)
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-4 p-3 bg-blue-50 dark:bg-navy-700 rounded-lg border border-blue-200 dark:border-navy-600">
                <h5 className="font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
                  <Icons.Calculator />
                  Hacker Performance Index (HPI)
                </h5>
                <p className="text-blue-800 dark:text-blue-200 text-sm">
                  Players are ranked into tiers using HPI, which combines: <strong>Average Quota</strong> (season average quota) + <strong>Skins Bonus</strong> (avg skins × 5) + <strong>CTP Bonus</strong> (avg CTPs × 8). Higher quota players get higher HPI scores, with additional bonuses for skins and CTPs to ensure well-rounded performance evaluation based on historical averages.
                </p>
              </div>

              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700/50">
                <h5 className="font-semibold text-amber-800 dark:text-amber-300 mb-2 flex items-center gap-2">
                  <Icons.User />
                  New Player Policy
                </h5>
                <p className="text-amber-800 dark:text-amber-200 text-sm">
                  <strong>New players are excluded from fantasy tiers</strong> until they complete at least 2 outings to establish reliable performance data. This ensures fair competition and accurate HPI calculations for tier placement. New players can still participate in regular golf events.
                </p>
              </div>

              <div className="mt-4 p-3 bg-blue-50 dark:bg-navy-700 rounded-lg border border-blue-200 dark:border-navy-600">
                <h5 className="font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
                  <Icons.Lock />
                  Tier Freeze Policy
                </h5>
                <p className="text-blue-800 dark:text-blue-200 text-sm">
                  Tiers freeze 24 hours before tee time to ensure fairness. After the freeze, new player registrations won't move existing players between tiers, guaranteeing that your picks remain valid in their assigned tiers.
                </p>
              </div>

              <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700/50">
                <h5 className="font-semibold text-green-800 dark:text-green-300 mb-2 flex items-center gap-2">
                  <Icons.Lightbulb />
                  Pro Tip
                </h5>
                <p className="text-green-800 dark:text-green-200 text-sm">
                  Check the <a href="/fantasy-leaderboard" className="underline font-medium hover:text-green-600 dark:hover:text-green-400">Fantasy Leaderboard</a> to see current standings and detailed weekly breakdowns! Study player performance trends to make better picks.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Payment Information Section - Collapsible */}
        {selectedEvent && isAuthenticated && user?.player_id && events.length > 0 && (
          <div className="bg-white dark:bg-navy-800 border border-gray-200 dark:border-navy-700 rounded-xl mt-6 shadow-lg overflow-hidden">
            {user?.season_paid ? (
              // Show paid status - always visible, not collapsible
              <div className="flex items-center justify-center space-x-3 p-4 bg-green-50 dark:bg-green-900/20">
                <div className="text-green-600 dark:text-green-400">
                  <Icons.CheckCircle className="w-6 h-6" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-bold text-green-800 dark:text-green-300">Fantasy Entry Fee Paid</h3>
                  <p className="text-green-700 dark:text-green-400 text-sm">You're all set for this season!</p>
                </div>
              </div>
            ) : (
              // Show payment options - collapsible
              <>
                <button
                  onClick={() => setIsPaymentExpanded(!isPaymentExpanded)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors"
                >
                  <h3 className="text-lg font-bold text-forest-900 dark:text-white flex items-center gap-2">
                    <span>💵</span>
                    Fantasy Entry Fee: ${PAYMENT_CONFIG.amount}
                  </h3>
                  <div className={`text-forest-600 dark:text-forest-400 transition-transform duration-200 ${isPaymentExpanded ? 'rotate-180' : ''}`}>
                    <Icons.ChevronDown />
                  </div>
                </button>

                {isPaymentExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-200 dark:border-navy-700">
                    <p className="text-gray-700 dark:text-gray-300 text-sm text-center mt-3 mb-4">Choose your preferred payment method</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* PayPal */}
                      <button
                        onClick={() => setShowPayPalModal(true)}
                        className="bg-white border-2 border-blue-200 hover:border-blue-300 rounded-lg p-4 transition-all active:scale-95 group"
                      >
                        <div className="flex flex-col items-center space-y-3">
                          <img src="/assets/paypal.svg" alt="PayPal" className="w-8 h-8" />
                          <div className="text-center">
                            <div className="font-semibold text-gray-800 group-hover:text-blue-600">PayPal</div>
                            <div className="text-xs text-gray-600">{PAYMENT_CONFIG.paypalEmail}</div>
                          </div>
                          <div className="text-xs text-blue-600 font-medium">Click to pay</div>
                        </div>
                      </button>

                      {/* Venmo */}
                      <button
                        onClick={() => setShowVenmoModal(true)}
                        className="bg-white border-2 border-purple-200 hover:border-purple-300 rounded-lg p-4 transition-all active:scale-95 group"
                      >
                        <div className="flex flex-col items-center space-y-3">
                          <img src="/assets/venmo.svg" alt="Venmo" className="w-8 h-8" />
                          <div className="text-center">
                            <div className="font-semibold text-gray-800 group-hover:text-purple-600">Venmo</div>
                            <div className="text-xs text-gray-600">{PAYMENT_CONFIG.venmoUsername}</div>
                          </div>
                          <div className="text-xs text-purple-600 font-medium">Click to pay</div>
                        </div>
                      </button>

                      {/* Zelle */}
                      <button
                        onClick={() => setShowZelleModal(true)}
                        className="bg-white border-2 border-green-200 hover:border-green-300 rounded-lg p-4 transition-all active:scale-95 group"
                      >
                        <div className="flex flex-col items-center space-y-3">
                          <img src="/assets/zelle.svg" alt="Zelle" className="w-8 h-8" />
                          <div className="text-center">
                            <div className="font-semibold text-gray-800 group-hover:text-green-600">Zelle</div>
                            <div className="text-xs text-gray-600">{PAYMENT_CONFIG.zelleEmail}</div>
                          </div>
                          <div className="text-xs text-green-600 font-medium">Send via banking app</div>
                        </div>
                      </button>

                      {/* Cold Hard Cash */}
                      <button
                        onClick={() => setShowCashModal(true)}
                        className="bg-white border-2 border-amber-200 hover:border-amber-300 rounded-lg p-4 transition-all active:scale-95 group"
                      >
                        <div className="flex flex-col items-center space-y-3">
                          <div className="text-amber-600 text-3xl">💰</div>
                          <div className="text-center">
                            <div className="font-semibold text-gray-800 group-hover:text-amber-600">Cold Hard Cash</div>
                            <div className="text-xs text-gray-600">Good ol' fashioned money</div>
                          </div>
                          <div className="text-xs text-amber-600 font-medium">Pay at the course</div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* PayPal Modal */}
      {showPayPalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowPayPalModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-blue-800 flex items-center gap-2">
                <img src="/assets/paypal.svg" alt="PayPal" className="w-6 h-6" />
                PayPal Payment
              </h3>
              <button
                onClick={() => setShowPayPalModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <Icons.Close className="w-6 h-6" />
              </button>
            </div>

            <div className="text-center">
              <div className="mb-4">
                <p className="text-gray-700 mb-2">Send ${PAYMENT_CONFIG.amount} to:</p>
                <p className="font-semibold text-gray-800">{PAYMENT_CONFIG.paypalEmail}</p>
              </div>

              <div className="mb-4">
                <img src="/assets/paypal-qr.png" alt="PayPal QR Code" className="w-48 h-48 mx-auto rounded-lg border border-gray-300" />
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => window.open(`https://www.paypal.com/ncp/payment/95KR2WZBG6TX4`, '_blank')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  Pay ${PAYMENT_CONFIG.amount} with PayPal
                </button>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Venmo Modal */}
      {showVenmoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowVenmoModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-purple-800 flex items-center gap-2">
                <img src="/assets/venmo.svg" alt="Venmo" className="w-6 h-6" />
                Venmo Payment
              </h3>
              <button
                onClick={() => setShowVenmoModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <Icons.Close className="w-6 h-6" />
              </button>
            </div>

            <div className="text-center">
              <div className="mb-4">
                <p className="text-gray-700 mb-2">Send ${PAYMENT_CONFIG.amount} to:</p>
                <p className="font-semibold text-gray-800">{PAYMENT_CONFIG.venmoUsername}</p>
              </div>

              <div className="mb-4">
                <img src="/assets/venmo-qr.png" alt="Venmo QR Code" className="w-48 h-48 mx-auto rounded-lg border border-gray-300" />
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => window.open(`https://venmo.com/${PAYMENT_CONFIG.venmoUsername.replace('@', '')}?txn=pay&amount=${PAYMENT_CONFIG.amount}&note=Fantasy Golf Entry`, '_blank')}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  Pay ${PAYMENT_CONFIG.amount} with Venmo
                </button>


              </div>
            </div>
          </div>
        </div>
      )}

      {/* Zelle Modal */}
      {showZelleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowZelleModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-green-800 flex items-center gap-2">
                <img src="/assets/zelle.svg" alt="Zelle" className="w-6 h-6" />
                Zelle Payment
              </h3>
              <button
                onClick={() => setShowZelleModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <Icons.Close className="w-6 h-6" />
              </button>
            </div>

            <div className="text-center">
              <div className="mb-4">
                <p className="text-gray-700 mb-2">Send ${PAYMENT_CONFIG.amount} to:</p>
                <p className="font-semibold text-gray-800">{PAYMENT_CONFIG.zelleEmail}</p>
              </div>

              <div className="mb-4">
                <img src="/assets/zelle-qr.png" alt="Zelle QR Code" className="w-48 h-48 mx-auto rounded-lg border border-gray-300" />
              </div>

              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-green-800 text-sm font-medium">
                    Use your banking app to send via Zelle
                  </p>
                  <p className="text-green-700 text-xs mt-1">
                    Look for "Send Money with Zelle" in your bank app
                  </p>
                </div>


              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cold Hard Cash Modal */}
      {showCashModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCashModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-amber-800 flex items-center gap-2">
                <span className="text-2xl">💰</span>
                Cold Hard Cash
              </h3>
              <button
                onClick={() => setShowCashModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <Icons.Close className="w-6 h-6" />
              </button>
            </div>

            <div className="text-center">
              <div className="mb-6">
                <div className="text-6xl mb-4">🤑</div>
                <p className="text-gray-700 mb-2 font-medium">Bring ${PAYMENT_CONFIG.amount} cash to the golf course</p>
                <p className="text-amber-700 text-sm font-semibold">Old school style! 💸</p>
              </div>

              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="text-amber-800 text-sm space-y-2">
                    <p className="font-medium flex items-center justify-center gap-2">
                      <span>🏌️</span>
                      <span>Find Jason at the course</span>
                    </p>
                    <p>Hand over the cold, hard cash</p>
                    <p className="text-xs text-amber-600">
                      (Exact change appreciated, but we'll figure it out!)
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-yellow-800 text-xs font-medium">
                    💡 Pro tip: Folding money works better than coins!
                  </p>
                </div>

                <div className="text-xs text-gray-500">
                  The most reliable payment method since... forever! 💪
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>

    <Footer />
    </div>
  );
};

export default FantasyGolf;
